import { FIELD_TYPE, SectionField } from "types/Models/Filing";
import { hasUploadOrLinkValue } from "./hasUploadOrLinkValue";
import { isConditionMet } from "./isConditionMet";
import { isHiddenForRole } from "./isHiddenForRole";

type Values = Record<string, unknown>;

export type CompletionField = Pick<
  SectionField,
  | "field_key"
  | "type"
  | "required"
  | "radio"
  | "is_hidden_for_customer"
  | "is_hidden_for_admin"
>;

export type SectionCompletion = { answered: number; total: number };

const hasValue = (field: CompletionField, values: Values) => {
  if (field.type === FIELD_TYPE.UPLOAD_OR_LINK) {
    return hasUploadOrLinkValue(field.field_key, values);
  }

  const value = values?.[field.field_key];

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  return value !== undefined && value !== null && String(value).trim() !== "";
};

/**
 * Counts visible mandatory questions and how many are answered.
 * A question with mandatory follow ups (e.g. an upload shown on "Yes")
 * only counts as answered once those follow ups are filled too.
 */
export const getSectionCompletion = ({
  fields,
  values,
  radioValues,
  isCustomer,
}: {
  fields: CompletionField[];
  values: Values;
  radioValues: Values;
  isCustomer: boolean;
}): SectionCompletion => {
  const visibleFields = fields.filter(
    (field) =>
      !isHiddenForRole(field, isCustomer) &&
      isConditionMet(field, radioValues, values)
  );
  const questions = visibleFields.filter(
    (field) => field.required && Object.keys(field.radio ?? {}).length === 0
  );
  const answered = questions.filter(
    (question) =>
      hasValue(question, values) &&
      visibleFields
        .filter((field) => field.required && field.radio?.[question.field_key])
        .every((followUp) => hasValue(followUp, values))
  );

  return { answered: answered.length, total: questions.length };
};
